import { useMemo } from "react";

/** Native <select> with optgroups — unified across staff/freelancer/production_partner, same
 * shape ({principal_type, principal_id, name, email}) for all three. Native select gives us
 * full keyboard operability + accessible labelling for free, no custom listbox needed. */
export function AssigneePicker({ principals, value, onChange, testId, autoFocus }) {
  const groups = useMemo(() => {
    const g = { staff: [], freelancer: [], production_partner: [] };
    (principals || []).forEach((p) => g[p.principal_type]?.push(p));
    return g;
  }, [principals]);

  const currentKey = value ? `${value.principal_type}:${value.principal_id}` : "";
  // Staff-only assignment (2026-02): a task assigned before that restriction can still carry a
  // freelancer/production_partner assignee — that option no longer exists in the list below, so
  // rather than let the browser silently fall back to the first real option (misleadingly
  // showing it as "selected"), we show a distinct disabled placeholder naming the reserved
  // assignee instead.
  const isReservedAssignee = value && value.principal_type !== "staff";

  return (
    <select
      data-testid={testId}
      aria-label="Assignee"
      autoFocus={autoFocus}
      value={isReservedAssignee ? "reserved" : currentKey}
      onChange={(e) => {
        const [principal_type, principal_id] = e.target.value.split(":");
        if (principal_type && principal_id) onChange({ principal_type, principal_id });
      }}
      className="rounded-md border border-hairline bg-paper px-2 py-1 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-brand/40"
    >
      <option value="" disabled>Assign to…</option>
      {isReservedAssignee && (
        <option value="reserved" disabled>{value.name} (reserved, staff-only now)</option>
      )}
      {groups.staff.length > 0 && (
        <optgroup label="Staff">
          {groups.staff.map((p) => <option key={`staff:${p.principal_id}`} value={`staff:${p.principal_id}`}>{p.name}</option>)}
        </optgroup>
      )}
      {groups.freelancer.length > 0 && (
        <optgroup label="Freelancers">
          {groups.freelancer.map((p) => <option key={`freelancer:${p.principal_id}`} value={`freelancer:${p.principal_id}`}>{p.name}</option>)}
        </optgroup>
      )}
      {groups.production_partner.length > 0 && (
        <optgroup label="Production Partners">
          {groups.production_partner.map((p) => <option key={`production_partner:${p.principal_id}`} value={`production_partner:${p.principal_id}`}>{p.name}</option>)}
        </optgroup>
      )}
    </select>
  );
}
